import { useState, useEffect } from "react";
import type { CoffeeInput, BrewRecipe } from "../types";
import { PROCESS_OPTIONS, GRADE_TAGS, KIND_OPTIONS, TEA_GRADE_TAGS, isTea, kindLabel } from "../types";

interface Props {
  initial?: Partial<CoffeeInput>;
  submitLabel?: string;
  onSubmit: (input: CoffeeInput) => Promise<void> | void;
  onCancel?: () => void;
}

const empty: CoffeeInput = {
  kind: "coffee",
  variety: "", process: "Washed", grams: 15, country: "", roaster: "",
  region: null, masl: null, farm: null, farmer: null, roaster_city: null,
  roast_date: null, roaster_sku: null, lot_number: null,
  grade: null, award_detail: null, award_score: null,
  flavor_tags: [], aroma_notes: null, finish_notes: null,
  brew_recipe: null, personal_notes: null, story: null, photo_url: null,
  portions: 1, freeze_date: null,
};

// AI sometimes returns null for required text fields (e.g. unknown roaster).
// Coercing those nulls back to empty strings before they hit the form state is critical
// because a null value flowing into v.variety.trim() throws a TypeError that silently
// kills the submit click handler — the dreaded "button does nothing" bug.
function sanitize(initial?: Partial<CoffeeInput>): CoffeeInput {
  const merged = { ...empty, ...(initial || {}) };
  for (const k of ["variety", "process", "country", "roaster"] as const) {
    if (merged[k] == null) (merged as Record<string, unknown>)[k] = "";
  }
  return merged;
}

export function CoffeeForm({ initial, submitLabel = "Save", onSubmit, onCancel }: Props) {
  const [v, setV] = useState<CoffeeInput>(sanitize(initial));
  const [tagsText, setTagsText] = useState((initial?.flavor_tags || []).join(", "));
  const [gradeTags, setGradeTags] = useState<string[]>(
    (initial?.grade || "").split(",").map((s) => s.trim()).filter(Boolean)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setV(sanitize(initial));
      setTagsText((initial.flavor_tags || []).join(", "));
      setGradeTags((initial.grade || "").split(",").map((s) => s.trim()).filter(Boolean));
    }
  }, [initial]);

  const tea = isTea(v.kind);
  // process is NOT NULL in the DB; for tea we auto-fill with the kind label so
  // the row still satisfies the constraint without forcing the user to pick.
  const processForSubmit = tea ? (v.process?.trim() || kindLabel(v.kind)) : v.process;

  const set = <K extends keyof CoffeeInput>(k: K, val: CoffeeInput[K]) => setV((s) => ({ ...s, [k]: val }));
  const setBrew = (key: keyof BrewRecipe, val: unknown) => {
    setV((s) => {
      const br = { ...(s.brew_recipe || {}) };
      if (val === "" || val === null || val === undefined) delete br[key];
      else (br as Record<string, unknown>)[key] = val;
      const empty = Object.keys(br).length === 0;
      return { ...s, brew_recipe: empty ? null : br };
    });
  };

  const setKind = (kind: string) => {
    setV((s) => ({
      ...s,
      kind,
      // When switching INTO tea, blank out coffee-specific defaults that won't
      // make sense; preserve user input on switch back to coffee.
      process: isTea(kind) ? "" : (s.process || "Washed"),
    }));
    // Grade vocab differs between coffee/tea; reset chips on kind change to avoid
    // leaving e.g. "COE" set on a matcha row.
    setGradeTags([]);
  };

  const reportError = (msg: string) => {
    setError(msg);
    // Make sure the user actually sees the error, even on a long form.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Use String(... ?? "") in case anything sneaks past sanitize (extra defensive).
    const missing: string[] = [];
    if (!String(v.variety ?? "").trim()) missing.push(tea ? "cultivar" : "variety");
    if (!String(v.country ?? "").trim()) missing.push("country");
    if (!String(v.roaster ?? "").trim()) missing.push(tea ? "producer" : "roaster");
    if (!tea && !String(v.process ?? "").trim()) missing.push("process");
    if (missing.length > 0) {
      reportError(`Missing required field${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}. Fill them in and tap save again.`);
      return;
    }
    setBusy(true);
    try {
      const flavor_tags = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
      const grade = gradeTags.join(",") || null;
      await onSubmit({ ...v, process: processForSubmit, flavor_tags, grade });
    } catch (e) {
      reportError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleGrade = (tag: string) => {
    setGradeTags((cur) => cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag]);
  };

  const gradeOptions = tea ? TEA_GRADE_TAGS : GRADE_TAGS;

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
      {error ? (
        <div className="md:col-span-2 border-2 border-red-500 bg-red-50 text-red-800 px-3 py-2 text-sm font-medium">
          {error}
        </div>
      ) : null}
      <Field label="Kind" full>
        <div className="flex flex-wrap gap-2">
          {KIND_OPTIONS.map((k) => (
            <button type="button" key={k} onClick={() => setKind(k)}
              className={`px-3 py-1 text-xs uppercase tracking-widest border ${v.kind === k ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}>
              {kindLabel(k)}
            </button>
          ))}
        </div>
      </Field>

      <Field label={tea ? "Cultivar *" : "Variety *"}><input className="input" value={v.variety} onChange={(e) => set("variety", e.target.value)} /></Field>
      {tea ? null : (
        <Field label="Process *">
          <input
            className="input"
            list="process-suggestions"
            value={v.process}
            onChange={(e) => set("process", e.target.value)}
            placeholder="Washed, White Honey, Anaerobic Natural…"
          />
          <datalist id="process-suggestions">
            {PROCESS_OPTIONS.map((p) => <option key={p} value={p} />)}
          </datalist>
        </Field>
      )}
      {tea ? null : (
        <Field label="Grams per portion"><input type="number" step="0.1" className="input" value={v.grams ?? 15} onChange={(e) => set("grams", Number(e.target.value))} /></Field>
      )}
      <Field label={tea ? "Bottles" : "Portions"}><input type="number" className="input" value={v.portions ?? 0} onChange={(e) => set("portions", Number(e.target.value))} /></Field>

      <Field label="Country *"><input className="input" value={v.country} onChange={(e) => set("country", e.target.value)} /></Field>
      <Field label="Region"><input className="input" value={v.region ?? ""} onChange={(e) => set("region", e.target.value || null)} /></Field>
      {tea ? null : (
        <Field label="MASL"><input type="number" className="input" value={v.masl ?? ""} onChange={(e) => set("masl", e.target.value ? Number(e.target.value) : null)} /></Field>
      )}
      <Field label={tea ? "Garden / farm" : "Farm"}><input className="input" value={v.farm ?? ""} onChange={(e) => set("farm", e.target.value || null)} /></Field>
      <Field label={tea ? "Tea master" : "Farmer"}><input className="input" value={v.farmer ?? ""} onChange={(e) => set("farmer", e.target.value || null)} /></Field>

      <Field label={tea ? "Producer / Brand *" : "Roaster *"}><input className="input" value={v.roaster} onChange={(e) => set("roaster", e.target.value)} /></Field>
      <Field label={tea ? "Producer city" : "Roaster city"}><input className="input" value={v.roaster_city ?? ""} onChange={(e) => set("roaster_city", e.target.value || null)} /></Field>
      <Field label={tea ? "Production / harvest date" : "Roast date"}><input type="date" className="input" value={v.roast_date ?? ""} onChange={(e) => set("roast_date", e.target.value || null)} /></Field>
      <Field label={tea ? "Storage date" : "Freeze date"}><input type="date" className="input" value={v.freeze_date ?? ""} onChange={(e) => set("freeze_date", e.target.value || null)} /></Field>
      <Field label={tea ? "SKU / Tin no." : "Roaster SKU"}><input className="input" value={v.roaster_sku ?? ""} onChange={(e) => set("roaster_sku", e.target.value || null)} /></Field>
      <Field label="Lot number"><input className="input" value={v.lot_number ?? ""} onChange={(e) => set("lot_number", e.target.value || null)} /></Field>

      <Field label={tea ? "Tea grade" : "Grade tags"} full>
        <div className="flex flex-wrap gap-2">
          {gradeOptions.map((t) => (
            <button type="button" key={t} onClick={() => toggleGrade(t)}
              className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${gradeTags.includes(t) ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}>
              {t}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Award detail"><input className="input" value={v.award_detail ?? ""} onChange={(e) => set("award_detail", e.target.value || null)} placeholder={tea ? "e.g. National Tea Fair Gold" : "e.g. 2024 COE 1st Place"}/></Field>
      {tea ? null : (
        <Field label="Award score"><input type="number" step="0.01" className="input" value={v.award_score ?? ""} onChange={(e) => set("award_score", e.target.value ? Number(e.target.value) : null)} /></Field>
      )}

      <Field label="Flavor tags (comma separated)" full>
        <input className="input" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder={tea ? "Umami, Seaweed, Sweet, Grassy" : "Jasmine, Mango, Bergamot"} />
      </Field>
      <Field label="Aroma"><input className="input" value={v.aroma_notes ?? ""} onChange={(e) => set("aroma_notes", e.target.value || null)} /></Field>
      <Field label="Finish"><input className="input" value={v.finish_notes ?? ""} onChange={(e) => set("finish_notes", e.target.value || null)} /></Field>

      <Field label={tea ? "Brew style" : "Brew method"}><input className="input" value={v.brew_recipe?.method ?? ""} onChange={(e) => setBrew("method", e.target.value)} placeholder={tea ? "Usucha / Koicha / Cold brew" : "V60 / Espresso"} /></Field>
      <Field label={tea ? "Tea / water ratio" : "Brew ratio"}><input className="input" value={v.brew_recipe?.ratio ?? ""} onChange={(e) => setBrew("ratio", e.target.value)} placeholder={tea ? "2g / 60ml" : "1:16"} /></Field>
      <Field label="Water temp (°C)"><input type="number" className="input" value={v.brew_recipe?.temp_c ?? ""} onChange={(e) => setBrew("temp_c", e.target.value ? Number(e.target.value) : "")} /></Field>
      <Field label={tea ? "Steep time (s)" : "Brew time (s)"}><input type="number" className="input" value={v.brew_recipe?.time_s ?? ""} onChange={(e) => setBrew("time_s", e.target.value ? Number(e.target.value) : "")} /></Field>
      <Field label="Brew notes" full><input className="input" value={v.brew_recipe?.notes ?? ""} onChange={(e) => setBrew("notes", e.target.value)} /></Field>

      <Field label="Story" full>
        <textarea
          className="input min-h-[100px]"
          value={v.story ?? ""}
          onChange={(e) => set("story", e.target.value || null)}
          placeholder="Roaster's narrative — origin context, producer story, processing details. Filled by AI on parse."
        />
      </Field>

      <Field label="Personal notes" full>
        <textarea className="input min-h-[80px]" value={v.personal_notes ?? ""} onChange={(e) => set("personal_notes", e.target.value || null)} />
      </Field>


      <div className="md:col-span-2 flex gap-2 justify-end">
        {onCancel ? <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button> : null}
        {/* type=button + manual onClick is more reliable on iOS than relying on the
            implicit <form onSubmit>; some Safari versions silently swallow the implicit
            submit when the form has many controls or is inside a flex layout. */}
        <button
          type="button"
          className="btn-primary"
          disabled={busy}
          onClick={(e) => submit(e as unknown as React.FormEvent)}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
